/**
 * DOCX ZIP reader — extracts and parses XML files from a DOCX archive.
 *
 * A DOCX file is a ZIP archive containing XML parts. This module handles
 * unzipping and parsing the relevant XML files into DOM Documents.
 */

import JSZip from "jszip";
import {
  Relationship,
  CommentDefinition,
  StyleDefinition,
  NumberingDefinition,
  NumberingLevel,
  RunProperties,
  ParagraphProperties,
  TableStyleConditional,
} from "./types";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export interface DocxPackage {
  /** Parsed word/document.xml */
  documentXml: Document;

  /** Parsed word/styles.xml (may be null if absent) */
  stylesXml: Document | null;

  /** Parsed word/numbering.xml (may be null if absent) */
  numberingXml: Document | null;

  /** Parsed word/comments.xml (may be null if absent) */
  commentsXml: Document | null;

  /** Parsed word/commentsExtended.xml (may be null if absent) — contains reply linkage */
  commentsExtendedXml: Document | null;

  /** Parsed word/footnotes.xml (may be null if absent) */
  footnotesXml: Document | null;

  /** Parsed word/endnotes.xml (may be null if absent) */
  endnotesXml: Document | null;

  /** Parsed word/_rels/document.xml.rels */
  relationshipsXml: Document | null;

  /** Parsed header XML files, keyed by relationship ID (rId) */
  headerXmls: Map<string, Document>;

  /** Parsed footer XML files, keyed by relationship ID (rId) */
  footerXmls: Map<string, Document>;

  /** Raw JSZip instance for extracting binary assets (images) */
  zip: JSZip;
}

/**
 * Read a DOCX file (as ArrayBuffer or Blob) and parse all relevant XML parts.
 */
export async function readDocxPackage(
  file: ArrayBuffer | Blob | Uint8Array
): Promise<DocxPackage> {
  const zip = await JSZip.loadAsync(file);

  const [
    documentXml,
    stylesXml,
    numberingXml,
    commentsXml,
    commentsExtendedXml,
    footnotesXml,
    endnotesXml,
    relationshipsXml,
  ] = await Promise.all([
    parseXmlPart(zip, "word/document.xml"),
    parseXmlPartOptional(zip, "word/styles.xml"),
    parseXmlPartOptional(zip, "word/numbering.xml"),
    parseXmlPartOptional(zip, "word/comments.xml"),
    parseXmlPartOptional(zip, "word/commentsExtended.xml"),
    parseXmlPartOptional(zip, "word/footnotes.xml"),
    parseXmlPartOptional(zip, "word/endnotes.xml"),
    parseXmlPartOptional(zip, "word/_rels/document.xml.rels"),
  ]);

  if (!documentXml) {
    throw new Error(
      "Invalid DOCX: word/document.xml not found in the archive."
    );
  }

  // Extract header/footer XML files based on relationships
  const headerXmls = new Map<string, Document>();
  const footerXmls = new Map<string, Document>();

  if (relationshipsXml) {
    const HEADER_TYPE =
      "http://schemas.openxmlformats.org/officeDocument/2006/relationships/header";
    const FOOTER_TYPE =
      "http://schemas.openxmlformats.org/officeDocument/2006/relationships/footer";
    const rels = relationshipsXml.getElementsByTagName("Relationship");

    const hfPromises: Promise<void>[] = [];
    for (let i = 0; i < rels.length; i++) {
      const el = rels[i];
      const id = el.getAttribute("Id") || "";
      const type = el.getAttribute("Type") || "";
      const target = el.getAttribute("Target") || "";

      if (type === HEADER_TYPE && id && target) {
        const path = target.startsWith("word/") ? target : `word/${target}`;
        hfPromises.push(
          parseXmlPartOptional(zip, path).then(doc => {
            if (doc) headerXmls.set(id, doc);
          })
        );
      } else if (type === FOOTER_TYPE && id && target) {
        const path = target.startsWith("word/") ? target : `word/${target}`;
        hfPromises.push(
          parseXmlPartOptional(zip, path).then(doc => {
            if (doc) footerXmls.set(id, doc);
          })
        );
      }
    }
    await Promise.all(hfPromises);
  }

  return {
    documentXml,
    stylesXml,
    numberingXml,
    commentsXml,
    commentsExtendedXml,
    footnotesXml,
    endnotesXml,
    relationshipsXml,
    headerXmls,
    footerXmls,
    zip,
  };
}

// ---------------------------------------------------------------------------
// XML part extraction helpers
// ---------------------------------------------------------------------------

const xmlParser = new DOMParser();

async function parseXmlPart(
  zip: JSZip,
  path: string
): Promise<Document | null> {
  const entry = zip.file(path);
  if (!entry) return null;
  const text = await entry.async("string");
  return xmlParser.parseFromString(text, "application/xml");
}

async function parseXmlPartOptional(
  zip: JSZip,
  path: string
): Promise<Document | null> {
  return parseXmlPart(zip, path);
}

// ---------------------------------------------------------------------------
// Relationship extraction
// ---------------------------------------------------------------------------

/**
 * Parse word/_rels/document.xml.rels into a Map of Relationship objects.
 */
export function extractRelationships(
  relsXml: Document | null
): Map<string, Relationship> {
  const map = new Map<string, Relationship>();
  if (!relsXml) return map;

  const rels = relsXml.getElementsByTagName("Relationship");
  for (let i = 0; i < rels.length; i++) {
    const el = rels[i];
    const id = el.getAttribute("Id") || "";
    const type = el.getAttribute("Type") || "";
    const target = el.getAttribute("Target") || "";
    const targetMode =
      (el.getAttribute("TargetMode") as "External" | "Internal") || undefined;
    map.set(id, { id, type, target, targetMode });
  }

  return map;
}

// ---------------------------------------------------------------------------
// Comment extraction
// ---------------------------------------------------------------------------

const W_NS = "http://schemas.openxmlformats.org/wordprocessingml/2006/main";

const W14_NS = "http://schemas.microsoft.com/office/word/2010/wordml";
const W15_NS = "http://schemas.microsoft.com/office/word/2012/wordml";

/**
 * Parse word/comments.xml into a Map of CommentDefinition objects.
 * Also extracts w14:paraId from the last paragraph of each comment
 * (used by commentsExtended.xml to link replies to parents).
 */
export function extractComments(
  commentsXml: Document | null
): Map<string, CommentDefinition> {
  const map = new Map<string, CommentDefinition>();
  if (!commentsXml) return map;

  const comments = commentsXml.getElementsByTagNameNS(W_NS, "comment");
  for (let i = 0; i < comments.length; i++) {
    const el = comments[i];
    const id = el.getAttributeNS(W_NS, "id") || el.getAttribute("w:id") || "";
    const author =
      el.getAttributeNS(W_NS, "author") || el.getAttribute("w:author") || "";
    const date =
      el.getAttributeNS(W_NS, "date") || el.getAttribute("w:date") || "";

    // Extract text content from all <w:t> elements within the comment
    const textEls = el.getElementsByTagNameNS(W_NS, "t");
    let content = "";
    for (let j = 0; j < textEls.length; j++) {
      content += textEls[j].textContent || "";
    }

    // Extract w14:paraId from the last <w:p> in the comment
    // This paraId is used by commentsExtended.xml to link replies
    let paraId: string | undefined;
    const paragraphs = el.getElementsByTagNameNS(W_NS, "p");
    if (paragraphs.length > 0) {
      const lastP = paragraphs[paragraphs.length - 1];
      paraId =
        lastP.getAttributeNS(W14_NS, "paraId") ||
        lastP.getAttribute("w14:paraId") ||
        undefined;
    }

    map.set(id, { id, author, date, content: content.trim(), paraId });
  }

  return map;
}

/**
 * Parse word/commentsExtended.xml to build a map of comment reply relationships.
 *
 * commentsExtended.xml contains <w15:commentEx> elements with:
 *   - w15:paraId — matches a comment's last paragraph paraId
 *   - w15:paraIdParent — the paraId of the parent comment (if this is a reply)
 *   - w15:done — whether the comment thread is resolved
 *
 * Returns a Map of child comment w:id → parent comment w:id.
 */
export function extractCommentReplyMap(
  commentsExtendedXml: Document | null,
  comments: Map<string, CommentDefinition>
): Map<string, string> {
  const replyMap = new Map<string, string>();
  if (!commentsExtendedXml) return replyMap;

  // Build a reverse lookup: paraId → comment w:id
  const paraIdToCommentId = new Map<string, string>();
  Array.from(comments.entries()).forEach(([commentId, def]) => {
    if (def.paraId) {
      paraIdToCommentId.set(def.paraId, commentId);
    }
  });

  // Parse <w15:commentEx> elements
  let commentExEls = commentsExtendedXml.getElementsByTagNameNS(
    W15_NS,
    "commentEx"
  );
  // Fallback for DOMParser that doesn't resolve namespaces
  if (commentExEls.length === 0) {
    commentExEls = commentsExtendedXml.getElementsByTagName("w15:commentEx");
  }

  for (let i = 0; i < commentExEls.length; i++) {
    const el = commentExEls[i];
    const paraId =
      el.getAttributeNS(W15_NS, "paraId") ||
      el.getAttribute("w15:paraId") ||
      "";
    const paraIdParent =
      el.getAttributeNS(W15_NS, "paraIdParent") ||
      el.getAttribute("w15:paraIdParent") ||
      "";

    if (paraId && paraIdParent) {
      const childCommentId = paraIdToCommentId.get(paraId);
      const parentCommentId = paraIdToCommentId.get(paraIdParent);
      if (childCommentId && parentCommentId) {
        replyMap.set(childCommentId, parentCommentId);
      }
    }
  }

  return replyMap;
}

// ---------------------------------------------------------------------------
// Style extraction
// ---------------------------------------------------------------------------

/**
 * Parse a borders container element from a style definition into a simple record.
 * Used for table style borders (both table-level and conditional formatting).
 */
function parseStyleBordersElement(bordersEl: Element): Record<string, string> {
  const result: Record<string, string> = {};
  const sides = [
    "top",
    "bottom",
    "left",
    "right",
    "insideH",
    "insideV",
    "start",
    "end",
  ];
  for (const side of sides) {
    const el = getChildNS(bordersEl, W_NS, side);
    if (!el) continue;
    const val =
      el.getAttributeNS(W_NS, "val") || el.getAttribute("w:val") || "";
    if (!val || val === "nil" || val === "none") continue;
    const sz = el.getAttributeNS(W_NS, "sz") || el.getAttribute("w:sz") || "4";
    const color =
      el.getAttributeNS(W_NS, "color") || el.getAttribute("w:color") || "auto";
    const sizeRaw = parseInt(sz, 10) || 4;
    if (sizeRaw === 0) continue;
    const sizePt = sizeRaw / 8;
    const hexColor = color && color !== "auto" ? `#${color}` : "#000000";
    const cssStyle =
      val === "double"
        ? "double"
        : val === "dashed"
          ? "dashed"
          : val === "dotted"
            ? "dotted"
            : "solid";
    const key = side === "start" ? "left" : side === "end" ? "right" : side;
    result[key] = `${sizePt}pt ${cssStyle} ${hexColor}`;
  }
  return result;
}

/**
 * Parse word/styles.xml into a Map of StyleDefinition objects.
 * Returns a tuple: [styles, styleNumIdMap] where styleNumIdMap links
 * style IDs to their associated numbering IDs.
 */
export function extractStyles(
  stylesXml: Document | null
): [Map<string, StyleDefinition>, Map<string, string>] {
  const styles = new Map<string, StyleDefinition>();
  const styleNumIdMap = new Map<string, string>();
  if (!stylesXml) return [styles, styleNumIdMap];

  const styleEls = stylesXml.getElementsByTagNameNS(W_NS, "style");
  for (let i = 0; i < styleEls.length; i++) {
    const el = styleEls[i];
    const styleId =
      el.getAttributeNS(W_NS, "styleId") || el.getAttribute("w:styleId") || "";
    const type = (el.getAttributeNS(W_NS, "type") ||
      el.getAttribute("w:type") ||
      "paragraph") as StyleDefinition["type"];

    const nameEl = getChildNS(el, W_NS, "name");
    const name =
      nameEl?.getAttributeNS(W_NS, "val") ||
      nameEl?.getAttribute("w:val") ||
      undefined;

    const basedOnEl = getChildNS(el, W_NS, "basedOn");
    const basedOn =
      basedOnEl?.getAttributeNS(W_NS, "val") ||
      basedOnEl?.getAttribute("w:val") ||
      undefined;

    const rPr = getChildNS(el, W_NS, "rPr");
    const pPr = getChildNS(el, W_NS, "pPr");

    const def: StyleDefinition = {
      styleId,
      name,
      type,
      basedOn,
      runProperties: rPr ? parseRunPropertiesFromElement(rPr) : undefined,
      paragraphProperties: pPr
        ? parseParagraphPropertiesFromElement(pPr)
        : undefined,
    };

    // Parse table style properties (conditional formatting: banded rows, header, etc.)
    if (type === "table") {
      // Whole-table cell shading from <w:tblPr><w:shd> or <w:tcPr><w:shd>
      const tblPr = getChildNS(el, W_NS, "tblPr");
      if (tblPr) {
        // Table-level borders from style
        const tblBordersEl = getChildNS(tblPr, W_NS, "tblBorders");
        if (tblBordersEl) {
          def.tableBorders = parseStyleBordersElement(tblBordersEl);
        }
      }
      const tcPr = getChildNS(el, W_NS, "tcPr");
      if (tcPr) {
        const shd = getChildNS(tcPr, W_NS, "shd");
        if (shd) {
          const fill =
            shd.getAttributeNS(W_NS, "fill") ||
            shd.getAttribute("w:fill") ||
            "";
          if (fill && fill !== "auto") def.tableCellShading = fill;
        }
      }

      // Parse <w:tblStylePr> conditional formatting entries
      const tblStylePrs = el.getElementsByTagNameNS(W_NS, "tblStylePr");
      if (tblStylePrs.length > 0) {
        def.tableStyleProperties = new Map();
        for (let j = 0; j < tblStylePrs.length; j++) {
          const tsp = tblStylePrs[j];
          // Only process direct children (not nested from basedOn)
          if (tsp.parentElement !== el) continue;
          const condType =
            tsp.getAttributeNS(W_NS, "type") ||
            tsp.getAttribute("w:type") ||
            "";
          if (!condType) continue;

          const cond: TableStyleConditional = {};

          // Cell shading
          const condTcPr = getChildNS(tsp, W_NS, "tcPr");
          if (condTcPr) {
            const condShd = getChildNS(condTcPr, W_NS, "shd");
            if (condShd) {
              const fill =
                condShd.getAttributeNS(W_NS, "fill") ||
                condShd.getAttribute("w:fill") ||
                "";
              if (fill && fill !== "auto") cond.cellShading = fill;
            }
            // Conditional borders
            const condTcBorders = getChildNS(condTcPr, W_NS, "tcBorders");
            if (condTcBorders) {
              cond.borders = parseStyleBordersElement(condTcBorders);
            }
          }

          // Run properties
          const condRPr = getChildNS(tsp, W_NS, "rPr");
          if (condRPr) {
            cond.runProperties = parseRunPropertiesFromElement(condRPr);
          }

          def.tableStyleProperties.set(condType, cond);
        }
      }
    }

    // Check for numbering link
    if (pPr) {
      const numPr = getChildNS(pPr, W_NS, "numPr");
      if (numPr) {
        const numIdEl = getChildNS(numPr, W_NS, "numId");
        const numId =
          numIdEl?.getAttributeNS(W_NS, "val") ||
          numIdEl?.getAttribute("w:val");
        if (numId) {
          styleNumIdMap.set(styleId, numId);
        }
      }
    }

    styles.set(styleId, def);
  }

  return [styles, styleNumIdMap];
}

// ---------------------------------------------------------------------------
// Numbering extraction
// ---------------------------------------------------------------------------

/**
 * Parse word/numbering.xml into a Map of NumberingDefinition objects.
 */
export function extractNumbering(
  numberingXml: Document | null
): Map<string, NumberingDefinition> {
  const map = new Map<string, NumberingDefinition>();
  if (!numberingXml) return map;

  // First, parse all abstractNum definitions
  const abstractNums = new Map<string, NumberingLevel[]>();
  const abstractNumEls = numberingXml.getElementsByTagNameNS(
    W_NS,
    "abstractNum"
  );
  for (let i = 0; i < abstractNumEls.length; i++) {
    const el = abstractNumEls[i];
    const abstractNumId =
      el.getAttributeNS(W_NS, "abstractNumId") ||
      el.getAttribute("w:abstractNumId") ||
      "";
    const levels: NumberingLevel[] = [];

    const lvlEls = el.getElementsByTagNameNS(W_NS, "lvl");
    for (let j = 0; j < lvlEls.length; j++) {
      const lvl = lvlEls[j];
      const level = parseInt(
        lvl.getAttributeNS(W_NS, "ilvl") || lvl.getAttribute("w:ilvl") || "0",
        10
      );

      const numFmtEl = getChildNS(lvl, W_NS, "numFmt");
      const numFmt =
        numFmtEl?.getAttributeNS(W_NS, "val") ||
        numFmtEl?.getAttribute("w:val") ||
        "decimal";

      const lvlTextEl = getChildNS(lvl, W_NS, "lvlText");
      const levelText =
        lvlTextEl?.getAttributeNS(W_NS, "val") ||
        lvlTextEl?.getAttribute("w:val") ||
        "";

      const startEl = getChildNS(lvl, W_NS, "start");
      const start = parseInt(
        startEl?.getAttributeNS(W_NS, "val") ||
          startEl?.getAttribute("w:val") ||
          "1",
        10
      );

      // Indent
      const pPr = getChildNS(lvl, W_NS, "pPr");
      const indEl = pPr ? getChildNS(pPr, W_NS, "ind") : null;
      const leftTwips = indEl
        ? parseInt(
            indEl.getAttributeNS(W_NS, "left") ||
              indEl.getAttribute("w:left") ||
              "0",
            10
          )
        : 0;

      levels.push({
        level,
        numFmt,
        levelText,
        start,
        indent: twipsToPoints(leftTwips),
      });
    }

    abstractNums.set(abstractNumId, levels);
  }

  // Then, parse num elements that reference abstractNum
  const numEls = numberingXml.getElementsByTagNameNS(W_NS, "num");
  for (let i = 0; i < numEls.length; i++) {
    const el = numEls[i];
    const numId =
      el.getAttributeNS(W_NS, "numId") || el.getAttribute("w:numId") || "";
    const abstractNumIdEl = getChildNS(el, W_NS, "abstractNumId");
    const abstractNumId =
      abstractNumIdEl?.getAttributeNS(W_NS, "val") ||
      abstractNumIdEl?.getAttribute("w:val") ||
      "";

    const levels = abstractNums.get(abstractNumId) || [];
    map.set(numId, { numId, abstractNumId, levels });
  }

  return map;
}

// ---------------------------------------------------------------------------
// Shared XML parsing helpers (used by other modules too)
// ---------------------------------------------------------------------------

/**
 * Check if an OOXML toggle property element is "on".
 *
 * Toggle properties like <w:b>, <w:i>, <w:strike> follow these rules:
 *   - Element absent → OFF (returns false for null input)
 *   - <w:b/> (no val attr) → ON
 *   - <w:b w:val="1"/> or <w:b w:val="true"/> → ON
 *   - <w:b w:val="0"/> or <w:b w:val="false"/> → OFF
 */
function isToggleOn(el: Element | null): boolean {
  if (!el) return false;
  const val = el.getAttributeNS(W_NS, "val") || el.getAttribute("w:val");
  if (val === null || val === undefined || val === "") return true; // no val attr → ON
  return val === "1" || val === "true";
}

/**
 * Parse <w:rPr> element into RunProperties.
 */
export function parseRunPropertiesFromElement(rPr: Element): RunProperties {
  const props: RunProperties = {};

  if (isToggleOn(getChildNS(rPr, W_NS, "b"))) props.bold = true;
  if (isToggleOn(getChildNS(rPr, W_NS, "i"))) props.italic = true;
  const uEl = getChildNS(rPr, W_NS, "u");
  if (uEl) {
    const uVal =
      uEl.getAttributeNS(W_NS, "val") || uEl.getAttribute("w:val") || "";
    // <w:u w:val="none"/> means explicitly no underline; any other value (or absent val) means underline
    if (uVal !== "none") props.underline = true;
  }
  if (isToggleOn(getChildNS(rPr, W_NS, "strike"))) props.strike = true;

  const vertAlignEl = getChildNS(rPr, W_NS, "vertAlign");
  if (vertAlignEl) {
    const val =
      vertAlignEl.getAttributeNS(W_NS, "val") ||
      vertAlignEl.getAttribute("w:val");
    if (val === "subscript") props.subscript = true;
    if (val === "superscript") props.superscript = true;
  }

  const colorEl = getChildNS(rPr, W_NS, "color");
  if (colorEl) {
    const val =
      colorEl.getAttributeNS(W_NS, "val") || colorEl.getAttribute("w:val");
    if (val && val !== "auto") props.color = val;
  }

  const highlightEl = getChildNS(rPr, W_NS, "highlight");
  if (highlightEl) {
    const val =
      highlightEl.getAttributeNS(W_NS, "val") ||
      highlightEl.getAttribute("w:val");
    if (val) props.highlight = highlightColorToHex(val);
  }

  const szEl = getChildNS(rPr, W_NS, "sz");
  if (szEl) {
    const val = szEl.getAttributeNS(W_NS, "val") || szEl.getAttribute("w:val");
    if (val) props.fontSize = parseInt(val, 10) / 2; // OOXML stores half-points
  }

  const fontEl = getChildNS(rPr, W_NS, "rFonts");
  if (fontEl) {
    // OOXML defines separate fonts for different character ranges:
    // w:ascii (Latin), w:hAnsi (high-ANSI Latin), w:eastAsia (CJK), w:cs (complex script)
    // Pick the first available — ascii for Latin text, eastAsia for CJK, hAnsi as fallback.
    const font =
      fontEl.getAttributeNS(W_NS, "ascii") ||
      fontEl.getAttribute("w:ascii") ||
      fontEl.getAttributeNS(W_NS, "eastAsia") ||
      fontEl.getAttribute("w:eastAsia") ||
      fontEl.getAttributeNS(W_NS, "hAnsi") ||
      fontEl.getAttribute("w:hAnsi");
    if (font) props.fontFamily = font;
  }

  return props;
}

/**
 * Parse <w:pPr> element into ParagraphProperties.
 */
export function parseParagraphPropertiesFromElement(
  pPr: Element
): ParagraphProperties {
  const props: ParagraphProperties = {};

  const pStyleEl = getChildNS(pPr, W_NS, "pStyle");
  if (pStyleEl) {
    props.styleId =
      pStyleEl.getAttributeNS(W_NS, "val") ||
      pStyleEl.getAttribute("w:val") ||
      undefined;
  }

  const jcEl = getChildNS(pPr, W_NS, "jc");
  if (jcEl) {
    const val = jcEl.getAttributeNS(W_NS, "val") || jcEl.getAttribute("w:val");
    if (val === "left" || val === "center" || val === "right" || val === "both")
      props.alignment = val;
  }

  const outlineLvlEl = getChildNS(pPr, W_NS, "outlineLvl");
  if (outlineLvlEl) {
    const val =
      outlineLvlEl.getAttributeNS(W_NS, "val") ||
      outlineLvlEl.getAttribute("w:val");
    if (val !== null && val !== undefined)
      props.outlineLevel = parseInt(val, 10);
  }

  // Numbering
  const numPr = getChildNS(pPr, W_NS, "numPr");
  if (numPr) {
    const numIdEl = getChildNS(numPr, W_NS, "numId");
    if (numIdEl)
      props.numId =
        numIdEl.getAttributeNS(W_NS, "val") ||
        numIdEl.getAttribute("w:val") ||
        undefined;

    const ilvlEl = getChildNS(numPr, W_NS, "ilvl");
    if (ilvlEl) {
      const val =
        ilvlEl.getAttributeNS(W_NS, "val") || ilvlEl.getAttribute("w:val");
      if (val !== null && val !== undefined) props.numLevel = parseInt(val, 10);
    }
  }

  // Spacing
  const spacingEl = getChildNS(pPr, W_NS, "spacing");
  if (spacingEl) {
    const before =
      spacingEl.getAttributeNS(W_NS, "before") ||
      spacingEl.getAttribute("w:before");
    if (before) props.spaceBefore = twipsToPoints(parseInt(before, 10));

    const after =
      spacingEl.getAttributeNS(W_NS, "after") ||
      spacingEl.getAttribute("w:after");
    if (after) props.spaceAfter = twipsToPoints(parseInt(after, 10));

    const line =
      spacingEl.getAttributeNS(W_NS, "line") ||
      spacingEl.getAttribute("w:line");
    const lineRule =
      spacingEl.getAttributeNS(W_NS, "lineRule") ||
      spacingEl.getAttribute("w:lineRule");
    if (line) {
      const lineVal = parseInt(line, 10);
      // Default lineRule is "auto" where 240 = single spacing
      if (!lineRule || lineRule === "auto") {
        props.lineSpacing = lineVal / 240;
      }
      // "exact" and "atLeast" use twips — we convert but approximate as multiplier
    }
  }

  // Indentation
  const indEl = getChildNS(pPr, W_NS, "ind");
  if (indEl) {
    const left =
      indEl.getAttributeNS(W_NS, "left") || indEl.getAttribute("w:left");
    if (left) {
      props.leftIndent = twipsToPoints(parseInt(left, 10));
      props.marginLeft = props.leftIndent;
    }

    const right =
      indEl.getAttributeNS(W_NS, "right") || indEl.getAttribute("w:right");
    if (right) props.rightIndent = twipsToPoints(parseInt(right, 10));

    const firstLine =
      indEl.getAttributeNS(W_NS, "firstLine") ||
      indEl.getAttribute("w:firstLine");
    if (firstLine)
      props.firstLineIndent = twipsToPoints(parseInt(firstLine, 10));

    const hanging =
      indEl.getAttributeNS(W_NS, "hanging") || indEl.getAttribute("w:hanging");
    if (hanging) props.firstLineIndent = -twipsToPoints(parseInt(hanging, 10));
  }

  return props;
}

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

/** Get the first direct child element with a given namespace and local name. */
export function getChildNS(
  parent: Element,
  ns: string,
  localName: string
): Element | null {
  const children = parent.childNodes;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (child.nodeType === 1) {
      const el = child as Element;
      if (el.localName === localName && el.namespaceURI === ns) return el;
      // Fallback: check w: prefixed name (for parsers that don't resolve NS)
      if (el.nodeName === `w:${localName}`) return el;
    }
  }
  return null;
}

/** Convert OOXML twips (1/20 of a point) to points. */
export function twipsToPoints(twips: number): number {
  return Math.round((twips / 20) * 100) / 100;
}

/** Convert OOXML EMUs (English Metric Units) to pixels (at 96 DPI). */
export function emuToPixels(emu: number): number {
  // 1 inch = 914400 EMU, 1 inch = 96 px at screen DPI
  return Math.round((emu / 914400) * 96);
}

/**
 * Convert OOXML highlight color name to hex value.
 * OOXML uses named colors for highlights (ST_HighlightColor).
 */
export function highlightColorToHex(name: string): string {
  const map: Record<string, string> = {
    black: "#000000",
    blue: "#0000FF",
    cyan: "#00FFFF",
    darkBlue: "#00008B",
    darkCyan: "#008B8B",
    darkGray: "#A9A9A9",
    darkGreen: "#006400",
    darkMagenta: "#8B008B",
    darkRed: "#8B0000",
    darkYellow: "#808000",
    green: "#00FF00",
    lightGray: "#D3D3D3",
    magenta: "#FF00FF",
    red: "#FF0000",
    white: "#FFFFFF",
    yellow: "#FFFF00",
  };
  return map[name] || name;
}

/** The main OOXML namespace for word processing */
export { W_NS };
